import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import Footer from "../SharedComponents/Footer";
import PlacesSection from "../SharedComponents/PlaceSection";
import Navbar from "./Navbar";
import CityWeatherPanel from "./CityWeatherPanel";
import BackButton from "./BackButton";
import { getCachedPlaceImageUrls } from "../services/placeImageCache";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface City {
    id: number;
    name: string;
    place_count: number;
    description?: string;
    image?: string;
    latitude?: number | null;
    longitude?: number | null;
}

interface SpecialExperience {
    place_id: string;
    name: string;
    description?: string;
    images?: { file_url: string }[];
}

const CITY_SECTIONS = [
    { title: (city: string) => `جاذبه‌های دیدنی ${city}`, category: "جاذبه‌های دیدنی" },
    { title: (city: string) => `رستوران‌های ${city}`, category: "رستوران" },
    { title: (city: string) => `کافه‌های ${city}`, category: "کافه" },
    { title: (city: string) => `اقامتگاه‌های ${city}`, category: "اقامتگاه" },
];

const CityPage: React.FC = () => {
    const { city } = useParams<{ city: string }>();
    const decodedCity = decodeURIComponent(city ?? "");

    const { data: cityData, isLoading: cityLoading } = useQuery<City[], Error, City | undefined>({
        queryKey: ["cities"],
        queryFn: async () => {
            const res = await fetch(`${BASE_URL}/api/cities/`);
            if (!res.ok) throw new Error("خطا در دریافت شهرها");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        select: (cities) => cities.find((c) => c.name === decodedCity),
        staleTime: 10 * 60 * 1000,
    });

    const imageUrl = cityData?.image
        ? cityData.image.startsWith("http")
            ? cityData.image
            : `${BASE_URL}${cityData.image}`
        : null;

    const hasImage = !!imageUrl;

    const { data: specialExperiences = [] } = useQuery<SpecialExperience[]>({
        queryKey: ["places", "special-experiences", decodedCity],
        queryFn: async () => {
            const params = new URLSearchParams({
                category: "تجربه خاص",
                city: decodedCity,
                page: "1",
                page_size: "10",
            });
            const res = await fetch(`${BASE_URL}/api/places/?${params.toString()}`);
            if (!res.ok) throw new Error("خطا در دریافت تجربه‌های خاص شهر");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        enabled: Boolean(decodedCity),
        staleTime: 5 * 60 * 1000,
    });
    const [activeExperienceIndex, setActiveExperienceIndex] = useState(0);

    useEffect(() => {
        setActiveExperienceIndex(0);
    }, [decodedCity]);

    useEffect(() => {
        if (activeExperienceIndex >= specialExperiences.length) {
            setActiveExperienceIndex(0);
        }
    }, [activeExperienceIndex, specialExperiences.length]);

    const activeExperience = specialExperiences[activeExperienceIndex];
    const [activeExperienceImageUrl, setActiveExperienceImageUrl] = useState<string | null>(null);
    const [isActiveExperienceImageLoading, setIsActiveExperienceImageLoading] = useState(false);

    useEffect(() => {
        if (!activeExperience) {
            setActiveExperienceImageUrl(null);
            setIsActiveExperienceImageLoading(false);
            return;
        }

        let isCurrentExperience = true;
        const inlineImageUrl = activeExperience.images
            ?.map((image) => image.file_url)
            .find(Boolean);

        if (inlineImageUrl) {
            setActiveExperienceImageUrl(
                inlineImageUrl.startsWith("http") ? inlineImageUrl : `${BASE_URL}${inlineImageUrl}`,
            );
            setIsActiveExperienceImageLoading(false);
            return () => {
                isCurrentExperience = false;
            };
        }

        setIsActiveExperienceImageLoading(true);
        getCachedPlaceImageUrls(activeExperience.place_id).then((imageUrls) => {
            const firstImageUrl = imageUrls[0];
            if (isCurrentExperience) {
                setActiveExperienceImageUrl(
                    firstImageUrl
                        ? (firstImageUrl.startsWith("http") ? firstImageUrl : `${BASE_URL}${firstImageUrl}`)
                        : null,
                );
                setIsActiveExperienceImageLoading(false);
            }
        });

        return () => {
            isCurrentExperience = false;
        };
    }, [activeExperience]);

    useEffect(() => {
        if (specialExperiences.length < 2) return;

        const rotationTimer = window.setInterval(() => {
            setActiveExperienceIndex((current) =>
                current === specialExperiences.length - 1 ? 0 : current + 1,
            );
        }, 5000);

        return () => window.clearInterval(rotationTimer);
    }, [specialExperiences.length]);

    return (
        <div className="min-h-screen min-w-screen">
            
            <Navbar />

            <div className="site-frame relative mt-[72px] h-72 overflow-hidden md:h-80 desktop:mt-20 desktop:h-96 desktop:rounded-2xl">

                {/* Background */}
                {cityLoading ? (
                    <div className="w-full h-full bg-gray-200 animate-pulse" />
                ) : hasImage ? (
                    <img
                        src={imageUrl!}
                        alt={decodedCity}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-100" />
                )}

                {/* Gradient — only with image */}
                {hasImage && !cityLoading && (
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/20" />
                )}

                {/* Back action and desktop breadcrumb */}
                <div className="site-container absolute inset-x-0 top-4 z-10 flex items-center justify-between gap-3 desktop:px-6">
                    <BackButton
                        variant={hasImage && !cityLoading ? "overlay" : "surface"}
                        fallbackTo="/destinations"
                        reverseArrow
                    />

                    {/* {!cityLoading && (
                        <nav className="hidden items-center gap-1.5 text-sm font-myYekanFaNumRegular tablet:flex">
                            <Link
                                to="/"
                                className={`transition ${hasImage ? "text-white/90 hover:text-white" : "text-gray-800 hover:text-gray-700"}`}
                            >
                                خانه
                            </Link>
                            <span className={hasImage ? "text-white/90" : "text-gray-800"}>&raquo;</span>
                            <Link
                                to="/destinations"
                                className={`transition ${hasImage ? "text-white/90 hover:text-white" : "text-gray-800 hover:text-gray-700"}`}
                            >
                                شهرها
                            </Link>
                            <span className={hasImage ? "text-white/90" : "text-gray-800"}>&raquo;</span>
                            <span className={`font-myYekanDemibold ${hasImage ? "text-white" : "text-gray-800"}`}>
                                {decodedCity}
                            </span>
                        </nav>
                    )} */}
                </div>

                {/* ── City name — anchored to bottom of hero ── */}
                {!cityLoading && (
                    <div className="site-container absolute bottom-0 inset-x-0 pb-6 desktop:px-6">
                        <h1 className={`text-2xl md:text-3xl lg:text-4xl font-myYekanFaNumExtraBold ${hasImage ? "text-white" : "text-gray-900"}`}>
                            {decodedCity}
                        </h1>
                        {!!cityData?.place_count && (
                            <span className={`text-sm font-myYekanFaNumRegular mt-1 block ${hasImage ? "text-white/55" : "text-gray-400"}`}>
                                {cityData.place_count} مکان
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* ── Page body ── */}
            <div className="max-w-8xl mx-auto">

                {(cityData?.description || activeExperience) && (
                    <section className="site-container pt-5 tablet:pt-6" aria-label={`معرفی ${decodedCity}`}>
                        <div className={`grid gap-5 ${activeExperience ? "desktop:grid-cols-[minmax(0,1fr)_320px]" : ""}`}>
                            {cityData?.description && (
                                <article className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm tablet:p-6">
                                    <div className="mb-4 flex items-center gap-2">
                                        {/* <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#f3fefa] text-[#038545]">
                                            <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                                        </span> */}
                                        <h2 className="font-myYekanFaNumDemiBold text-lg text-gray-950">
                                            درباره {decodedCity}
                                        </h2>
                                    </div>
                                    <div className="prose prose-sm max-w-none text-justify font-myYekanFaNumRegular leading-7 text-black tablet:prose-base
                                        prose-p:text-black prose-li:text-black
                                        prose-headings:font-myYekanDemibold prose-headings:text-black
                                        prose-strong:text-black prose-strong:font-myYekanDemibold
                                        prose-a:text-primary-500 prose-a:no-underline hover:prose-a:underline
                                        prose-li:marker:text-gray-400">
                                        <ReactMarkdown>{cityData.description}</ReactMarkdown>
                                    </div>
                                </article>
                            )}

                            {activeExperience && (
                                <aside className={`relative min-h-[280px] overflow-hidden rounded-2xl text-white shadow-sm ${isActiveExperienceImageLoading && !activeExperienceImageUrl ? "bg-slate-900" : "bg-[#038545]"}`}>
                                    {activeExperienceImageUrl && (
                                        <img
                                            src={activeExperienceImageUrl}
                                            alt=""
                                            aria-hidden="true"
                                            className="absolute inset-0 z-0 h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                    )}
                                    {activeExperienceImageUrl && (
                                        <div className="absolute inset-0 z-10 bg-black/55" aria-hidden="true" />
                                    )}
                                    {isActiveExperienceImageLoading && !activeExperienceImageUrl && (
                                        <div className="absolute inset-0 z-10 bg-white/10 p-5 backdrop-blur-md" aria-hidden="true">
                                            <div className="h-5 w-24 animate-pulse rounded-full bg-white/25" />
                                            <div className="mt-5 h-6 w-3/4 animate-pulse rounded bg-white/25" />
                                            <div className="mt-3 h-3 w-full animate-pulse rounded bg-white/15" />
                                            <div className="mt-2 h-3 w-4/5 animate-pulse rounded bg-white/15" />
                                        </div>
                                    )}
                                    <div className={`relative z-20 flex min-h-[280px] flex-col p-5 ${isActiveExperienceImageLoading && !activeExperienceImageUrl ? "opacity-0" : ""}`}>
                                        <span className="inline-flex w-fit rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-myYekanFaNumMedium">
                                           تجربه خاص در {decodedCity}
                                        </span>
                                        <h2 className="mt-3 line-clamp-2 font-myYekanFaNumDemiBold text-lg leading-7">
                                            {activeExperience.name}
                                        </h2>
                                        {activeExperience.description && (
                                            <p className="mt-2 line-clamp-3 text-xs leading-6 text-white/85">
                                                {activeExperience.description}
                                            </p>
                                        )}
                                        <div className="mt-auto pt-4">
                                            <Link
                                                to={`/places/${activeExperience.place_id}`}
                                                className="rounded-lg bg-[#cffae7] px-3 py-2 text-xs font-myYekanFaNumMedium text-[#075c35] transition-colors hover:bg-white"
                                            >
                                                مشاهده تجربه
                                            </Link>
                                        </div>
                                    </div>
                                </aside>
                            )}
                        </div>
                    </section>
                )}

                {cityData?.id && (
                    <CityWeatherPanel
                        cityId={cityData.id}
                        cityName={cityData.name}
                        coordinatesAvailable={cityData.latitude != null && cityData.longitude != null}
                    />
                )}

                <div className="mt-8">
                    {/* Place sections */}
                    {CITY_SECTIONS.map(({ title, category }) => (
                        <PlacesSection
                            key={category}
                            title={title(decodedCity)}
                            category={category}
                            city={decodedCity}
                            hideIfEmpty
                            showArrowsOnHover
                        />
                    ))}
                </div>


            </div>

            <Footer />
        </div>
    );
};

export default CityPage;
